/* Date-range presets, computed in the tenant's timezone.

   The PBX stamps calls in the tenant's timezone and the backend filters on
   YYYY-MM-DD in that same zone, so "today" has to mean today in the tenant's
   zone — not in whatever timezone the viewer's browser happens to sit. That
   keeps the presets, the display, and the PBX's own date filtering aligned.
   See lib/timezone.js for where the zone comes from. */

import { ymd } from './timezone.js';

/** Today's date in the tenant timezone, as YYYY-MM-DD. */
export function today() {
  return ymd(new Date());
}

/** Yesterday's date in the tenant timezone, as YYYY-MM-DD. */
export function yesterday() {
  return addDays(today(), -1);
}

/** The tenant-timezone calendar date of an ISO instant, as YYYY-MM-DD. */
export function localDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : ymd(d);
}

/* Date maths is done on UTC-midnight anchors so adding days can't be knocked
   sideways by daylight saving. The strings going in and out are calendar
   dates, not instants, so this never touches a real clock. */
function parse(ymd) {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

function format(date) {
  return date.toISOString().slice(0, 10);
}

function addDays(ymd, n) {
  const d = parse(ymd);
  d.setUTCDate(d.getUTCDate() + n);
  return format(d);
}

function firstOfMonth(ymd) {
  const d = parse(ymd);
  return format(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)));
}

/** Monday of the week containing `ymd`. UK weeks start Monday. */
function mondayOf(ymd) {
  const dow = parse(ymd).getUTCDay(); // 0 Sun … 6 Sat
  const backToMonday = (dow + 6) % 7; // Mon→0, Sun→6
  return addDays(ymd, -backToMonday);
}

/**
 * The presets offered in the UI. Each returns { from, to }, inclusive.
 *
 * Order matters — it's the order the buttons render in.
 */
export const PRESETS = [
  {
    key: 'today',
    label: 'Today',
    range: () => {
      const t = today();
      return { from: t, to: t };
    },
  },
  {
    key: 'yesterday',
    label: 'Yesterday',
    range: () => {
      const y = addDays(today(), -1);
      return { from: y, to: y };
    },
  },
  {
    key: 'this-week',
    label: 'This week',
    range: () => {
      const t = today();
      return { from: mondayOf(t), to: t };
    },
  },
  {
    key: 'last-week',
    label: 'Last week',
    range: () => {
      const lastMonday = addDays(mondayOf(today()), -7);
      return { from: lastMonday, to: addDays(lastMonday, 6) };
    },
  },
  {
    key: 'last-month',
    label: 'Last month',
    range: () => {
      const lastDay = addDays(firstOfMonth(today()), -1);
      return { from: firstOfMonth(lastDay), to: lastDay };
    },
  },
];

/** The default range when Call history first opens. */
export function defaultRange() {
  return PRESETS[0].range();
}

/** Which preset (if any) a given range corresponds to — for highlighting. */
export function matchPreset(range) {
  return PRESETS.find((p) => {
    const r = p.range();
    return r.from === range.from && r.to === range.to;
  })?.key ?? null;
}

/** "17 Jul 2026" from YYYY-MM-DD, for the footnote. */
export function prettyDate(ymd) {
  if (!ymd) return '';
  return parse(ymd).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
  });
}
